import type { Tasting } from '../../types'
import { isTasterComplete } from '../../utils/scoring'
import { CorrelationHeatmap } from './CorrelationHeatmap'
import { WineRankingChart } from './WineRankingChart'

type ChartsTabProps = {
  tasting: Tasting
}

export function ChartsTab({ tasting }: ChartsTabProps) {
  const completeTasters = tasting.tasters.filter(isTasterComplete)
  const hasCompleteData = completeTasters.length >= 2

  if (!hasCompleteData) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="p-4 bg-orange-100 border border-orange-400 rounded-lg">
          <p className="font-medium mb-2">Incomplete Data</p>
          <p className="text-sm text-orange-800">
            {completeTasters.length === 0
              ? 'No tasters have completed their scores yet. Charts require at least 2 tasters with complete scores.'
              : 'Only one taster has completed their scores. Charts require at least 2 tasters with complete scores to show correlations.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-6 max-w-6xl mx-auto">
      <section>
        <h2 className="text-lg font-semibold mb-3">Wine Rankings</h2>
        <p className="text-sm text-gray-600 mb-4">
          Total points per wine (lower is better).
        </p>
        <div className="bg-white border border-black/10 rounded-lg p-4">
          <WineRankingChart tasting={tasting} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Correlation Heatmap</h2>
        <p className="text-sm text-gray-600 mb-4">
          Shows how similar each pair of tasters' rankings were using Spearman correlation (-1 to +1).
        </p>
        <div className="bg-white border border-black/10 rounded-lg p-4">
          <CorrelationHeatmap tasters={tasting.tasters} />
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-green-600"></span>
            <span>High agreement (+0.7 to +1.0)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-green-200 border border-green-300"></span>
            <span>Moderate agreement (+0.3 to +0.7)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-gray-100 border border-gray-300"></span>
            <span>No correlation (-0.3 to +0.3)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-red-200 border border-red-300"></span>
            <span>Moderate disagreement (-0.7 to -0.3)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-red-600"></span>
            <span>Strong disagreement (-1.0 to -0.7)</span>
          </div>
        </div>
      </section>
    </div>
  )
}
